import type { ThemeConfig } from 'antd';

/**
 * The product design (the "console" design system) as an Ant Design 5 theme.
 * Every value traces to a design token (named in the trailing comment); the
 * same tokens live as CSS variables in lib.styles/src/desk.scss.
 * ResponsiveAntdProvider hands it to the root ConfigProvider everywhere but
 * the Match.Express views, which keep their own skin.
 */
export const themeDesk: ThemeConfig = {
  token: {
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 14,
    colorPrimary: '#4e67ea', // primary
    colorPrimaryHover: '#3e52bb', // primary-hover
    colorPrimaryActive: '#2f3e8c', // primary-active
    colorInfo: '#4e67ea', // primary
    colorLink: '#4e67ea', // primary
    colorLinkHover: '#3e52bb', // primary-hover
    colorLinkActive: '#2f3e8c', // primary-active
    colorSuccess: '#0e7a4d', // action
    colorError: '#f5222d', // danger
    colorWarning: '#faad14', // warning
    colorText: '#1b1c1e', // ink
    colorTextHeading: '#1b1c1e', // ink
    colorTextSecondary: '#57585a', // ink-2
    colorTextTertiary: '#6b6c6f', // ink-3
    colorTextDescription: '#57585a', // ink-2
    colorTextPlaceholder: '#898a8c', // placeholder
    colorTextDisabled: '#c0c1c3', // ink-disabled
    colorTextQuaternary: '#c0c1c3', // ink-disabled
    colorBorder: '#d9dadc', // line-strong
    colorBorderSecondary: '#e8e9eb', // line
    colorSplit: '#e8e9eb', // line
    colorBgLayout: '#fafafa', // ground
    colorBgContainer: '#ffffff', // on-solid
    colorBgElevated: '#ffffff', // on-solid
    colorBgSpotlight: '#1b1c1e', // ink
    colorBgContainerDisabled: '#f2f3f5', // panel
    colorFillTertiary: '#f2f3f5', // panel
    colorFillQuaternary: '#fafafa', // ground
    colorBgMask: 'rgba(15, 18, 30, 0.42)', // scrim
    borderRadius: 10,
    borderRadiusXS: 6,
    borderRadiusSM: 8,
    borderRadiusLG: 14,
    controlHeight: 36,
    controlHeightSM: 30,
    controlHeightLG: 42,
    controlOutline: '#dce1fb', // primary-wash
    controlOutlineWidth: 3,
    controlItemBgHover: '#fafafa', // ground
    controlItemBgActive: '#f2f3f5', // panel
    controlItemBgActiveHover: '#f2f3f5', // panel
    boxShadow: '0 16px 48px -16px rgba(15, 18, 30, 0.22), 0 2px 6px rgba(15, 18, 30, 0.06)', // shadow-float
    boxShadowSecondary:
      '0 16px 48px -16px rgba(15, 18, 30, 0.22), 0 2px 6px rgba(15, 18, 30, 0.06)', // shadow-float
    boxShadowTertiary: '0 1px 2px rgba(15, 18, 30, 0.05)', // shadow-rest
    motionDurationFast: '0.12s',
    motionDurationMid: '0.15s',
    motionDurationSlow: '0.24s',
    motionEaseOut: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
    motionEaseInOut: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
    wireframe: false,
  },
  components: {
    Button: {
      borderRadius: 10,
      borderRadiusSM: 8,
      borderRadiusLG: 12,
      controlHeight: 36,
      controlHeightSM: 30,
      controlHeightLG: 42,
      paddingInline: 14,
      paddingInlineSM: 10,
      paddingInlineLG: 18,
      contentFontSize: 14,
      contentFontSizeSM: 13,
      contentFontSizeLG: 15,
      fontWeight: 500,
      defaultBg: '#ffffff', // on-solid
      defaultColor: '#1b1c1e', // ink
      defaultBorderColor: '#d9dadc', // line-strong
      defaultHoverBg: '#fafafa', // ground
      defaultHoverColor: '#1b1c1e', // ink
      defaultHoverBorderColor: '#d9dadc', // line-strong
      defaultActiveBg: '#f2f3f5', // panel
      defaultActiveColor: '#1b1c1e', // ink
      defaultActiveBorderColor: '#d9dadc', // line-strong
      defaultShadow: '0 1px 2px rgba(15, 18, 30, 0.05)', // shadow-rest
      primaryShadow: 'none',
      dangerShadow: 'none',
      borderColorDisabled: '#e8e9eb', // line
      textTextColor: '#1b1c1e', // ink
      textTextHoverColor: '#1b1c1e', // ink
      textTextActiveColor: '#1b1c1e', // ink
      textHoverBg: '#f2f3f5', // panel
      colorBgTextActive: '#f2f3f5', // panel
      linkHoverBg: 'transparent',
      onlyIconSize: 20,
      onlyIconSizeSM: 16,
      onlyIconSizeLG: 20,
      colorError: '#f5222d', // danger
      colorErrorHover: '#c21820', // danger-hover
      colorErrorActive: '#c21820', // danger-hover
      colorErrorBorderHover: '#c21820', // danger-hover
    },
    Input: {
      borderRadius: 10,
      controlHeight: 36,
      hoverBorderColor: '#c0c1c3', // ink-disabled
      activeBorderColor: '#4e67ea', // primary
      activeShadow: '0 0 0 3px #dce1fb', // shadow-focus
    },
    InputNumber: {
      borderRadius: 10,
      controlHeight: 36,
      hoverBorderColor: '#c0c1c3', // ink-disabled
      activeBorderColor: '#4e67ea', // primary
      activeShadow: '0 0 0 3px #dce1fb', // shadow-focus
    },
    DatePicker: {
      borderRadius: 10,
      controlHeight: 36,
      hoverBorderColor: '#c0c1c3', // ink-disabled
      activeBorderColor: '#4e67ea', // primary
      activeShadow: '0 0 0 3px #dce1fb', // shadow-focus
      cellHoverBg: '#fafafa', // ground
    },
    Select: {
      borderRadius: 10,
      controlHeight: 36,
      hoverBorderColor: '#c0c1c3', // ink-disabled
      activeBorderColor: '#4e67ea', // primary
      activeOutlineColor: '#dce1fb', // primary-wash
      optionSelectedBg: '#f2f3f5', // panel
      optionActiveBg: '#fafafa', // ground
      optionSelectedFontWeight: 500,
      optionHeight: 34,
      optionPadding: '7px 12px',
    },
    Segmented: {
      trackBg: '#f2f3f5', // panel
      trackPadding: 3,
      itemColor: '#57585a', // ink-2
      itemHoverColor: '#1b1c1e', // ink
      itemHoverBg: 'transparent',
      itemActiveBg: 'transparent',
      itemSelectedBg: '#ffffff', // on-solid
      itemSelectedColor: '#1b1c1e', // ink
      borderRadius: 10,
      borderRadiusSM: 8,
      controlHeight: 38,
      fontSize: 13.5,
      controlPaddingHorizontal: 12,
    },
    Tag: {
      defaultBg: '#f2f3f5', // panel
      defaultColor: '#57585a', // ink-2
      colorBorder: 'transparent',
      borderRadiusSM: 999,
      fontSizeSM: 13,
      lineHeightSM: 1.6923,
    },
    Table: {
      headerBg: '#ffffff', // on-solid
      headerColor: '#6b6c6f', // ink-3
      headerSplitColor: 'transparent',
      rowHoverBg: '#fafafa', // ground
      borderColor: '#e8e9eb', // line
      cellPaddingBlock: 12,
      cellPaddingInline: 12,
      footerBg: '#ffffff', // on-solid
      headerBorderRadius: 0,
      rowSelectedBg: '#fafafa', // ground
      rowSelectedHoverBg: '#fafafa', // ground
    },
    Modal: {
      borderRadiusLG: 16,
      titleFontSize: 18,
      titleLineHeight: 1.4444,
      titleColor: '#1b1c1e', // ink
      contentBg: '#ffffff', // on-solid
      headerBg: '#ffffff', // on-solid
    },
    Drawer: {
      colorBgMask: 'rgba(15, 18, 30, 0.42)', // scrim
    },
    Tooltip: {
      colorBgSpotlight: '#1b1c1e', // ink
      colorTextLightSolid: '#ffffff', // on-solid
      borderRadius: 10,
      fontSize: 12.5,
      paddingSM: 16,
      paddingXS: 12,
    },
    Popover: {
      borderRadiusLG: 12,
    },
    Dropdown: {
      borderRadiusLG: 12,
      borderRadiusSM: 8,
      controlItemBgHover: '#fafafa', // ground
      controlItemBgActive: '#f2f3f5', // panel
      controlItemBgActiveHover: '#f2f3f5', // panel
    },
    Message: {
      contentBg: '#1b1c1e', // ink
      colorText: '#ffffff', // on-solid
      contentPadding: '10px 16px',
      borderRadiusLG: 12,
      fontSize: 13.5,
    },
    Menu: {
      itemHeight: 38,
      itemBorderRadius: 10,
      itemColor: '#57585a', // ink-2
      itemHoverColor: '#1b1c1e', // ink
      itemHoverBg: '#fafafa', // ground
      itemSelectedBg: '#f2f3f5', // panel
      itemSelectedColor: '#1b1c1e', // ink
      itemActiveBg: '#f2f3f5', // panel
      iconSize: 20,
      collapsedIconSize: 22,
      collapsedWidth: 56,
      itemMarginBlock: 2,
      itemMarginInline: 0,
      activeBarBorderWidth: 0,
      iconMarginInlineEnd: 10,
      fontSize: 14,
    },
    Layout: {
      siderBg: '#ffffff', // on-solid
      lightSiderBg: '#ffffff', // on-solid
      headerBg: '#ffffff', // on-solid
      headerHeight: 60,
      headerPadding: '0 28px',
      headerColor: '#1b1c1e', // ink
      bodyBg: '#fafafa', // ground
    },
    Card: {
      borderRadiusLG: 14,
      boxShadowTertiary: '0 1px 2px rgba(15, 18, 30, 0.05)', // shadow-rest
      colorBorderSecondary: '#e8e9eb', // line
      headerFontSize: 16,
      bodyPadding: 16,
    },
    Alert: {
      borderRadiusLG: 12,
    },
    Badge: {
      dotSize: 8,
      colorError: '#ff4d4f', // notice
    },
    Avatar: {
      colorTextPlaceholder: '#1a2440', // navy-mark
      borderRadius: 10,
      containerSize: 32,
      textFontSize: 13,
    },
    Form: {
      itemMarginBottom: 16,
      labelColor: '#57585a', // ink-2
      verticalLabelPadding: '0 0 6px',
    },
    Checkbox: {
      borderRadiusSM: 6,
    },
    Slider: {
      railBg: '#f2f3f5', // panel
      railHoverBg: '#e8e9eb', // line
      trackBg: '#4e67ea', // primary
      trackHoverBg: '#3e52bb', // primary-hover
      handleColor: '#4e67ea', // primary
      handleActiveColor: '#2f3e8c', // primary-active
      dotBorderColor: '#d9dadc', // line-strong
      dotActiveBorderColor: '#4e67ea', // primary
    },
    Progress: {
      defaultColor: '#4e67ea', // primary
      remainingColor: '#f2f3f5', // panel
    },
    Skeleton: {
      gradientFromColor: '#f2f3f5', // panel
      gradientToColor: '#fafafa', // ground
      blockRadius: 8,
    },
    Collapse: {
      headerBg: '#ffffff', // on-solid
      contentBg: '#ffffff', // on-solid
      borderRadiusLG: 14,
      headerPadding: '12px 16px',
    },
    Statistic: {
      titleFontSize: 13.5,
      contentFontSize: 20,
    },
    Empty: {
      colorTextDescription: '#57585a', // ink-2
    },
    Upload: {
      actionsColor: '#6b6c6f', // ink-3
    },
    Steps: {
      dotSize: 8,
    },
  },
};
